import { useEffect, useState } from "react";
import type { ConsentDescription } from "@cloudflare/workers-oauth-provider";
import { api, type Room } from "./api";
import { canEdit } from "../shared/authorization";
import "./agent-oauth.css";

type Consent = { handle: string; details: ConsentDescription };
const ALL = "*";
export function AgentOAuth({ userName }: { userName: string }) {
  const [consent, setConsent] = useState<Consent | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [boardId, setBoardId] = useState("");
  const [permission, setPermission] = useState("read");
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.all([
      api<Consent>("/api/agent-oauth/begin", { query: location.search }),
      api<Room[]>("/api/rooms"),
    ])
      .then(([next, projects]) => {
        if (live) {
          setConsent(next);
          setRooms(projects);
        }
      })
      .catch((e) => {
        if (live) setError(e.message);
      });
    return () => {
      live = false;
    };
  }, []);
  async function decide(decision: "approve" | "deny") {
    if (!consent) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ redirectTo: string }>(
        "/api/agent-oauth/decide",
        {
          handle: consent.handle,
          decision,
          boardId: boardId === ALL ? null : boardId,
          permission,
          offline,
        },
      );
      location.assign(result.redirectTo);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  const all = boardId === ALL;
  const selected = rooms.find((r) => r.id === boardId);
  const editable = all
    ? rooms.some((r) => canEdit(r.role))
    : Boolean(selected && canEdit(selected.role));
  const archived = rooms.filter((r) => r.archivedAt);
  return (
    <main className="agent-oauth">
      <p className="agent-oauth-brand">CALQUE · AGENT CONNECTION</p>
      <h1>Choose what your agent can access</h1>
      <p>
        Signed in as <strong>{userName}</strong>.
      </p>
      {consent ? (
        <>
          <section
            className="agent-oauth-client"
            aria-label="Connecting application"
          >
            <strong>{consent.details.clientName}</strong>
            <p>
              {consent.details.clientDomain ? (
                <>
                  Published by <strong>{consent.details.clientDomain}</strong>.
                </>
              ) : (
                "This application’s name is self-reported."
              )}{" "}
              Access returns to <strong>{consent.details.redirectHost}</strong>.
            </p>
            {consent.details.redirectIsLoopback && (
              <p>
                Continue only if you just started connecting an app on this
                computer.
              </p>
            )}
          </section>
          <label className="agent-oauth-field">
            Project
            <select
              value={boardId}
              onChange={(e) => {
                setBoardId(e.target.value);
                setPermission("read");
              }}
              disabled={busy}
            >
              <option value="">Choose access</option>
              <option value={ALL}>All your projects, including new ones</option>
              <optgroup label="One project">
                {rooms
                  .filter((room) => !room.archivedAt)
                  .map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
              </optgroup>
              {archived.length > 0 && (
                <optgroup label="Archived">
                  {archived.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <label className="agent-oauth-field">
            Permission
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value)}
              disabled={busy || !boardId}
            >
              <option value="read">Read only</option>
              {editable && (
                <option value="edit">
                  {all ? "Read and edit where you can edit" : "Read and edit"}
                </option>
              )}
            </select>
          </label>
          <label className="agent-oauth-check">
            <input
              type="checkbox"
              checked={offline}
              onChange={(e) => setOffline(e.target.checked)}
              disabled={busy}
            />{" "}
            Keep connected for up to 30 days
          </label>
          <ul>
            <li>
              {permission === "edit"
                ? "Read sheets and discussions, and make editable changes as you."
                : "Read sheets, editable content and discussions."}
            </li>
            <li>
              {offline
                ? "The agent can renew its access automatically, even after you close this tab."
                : "Access lasts 15 minutes. Connect again when it expires."}
            </li>
            <li>
              {all
                ? "Every project you can open, now or later, at your role in each. The agent names a project on each call."
                : "Only the selected project."}{" "}
              No invitations or account changes.
            </li>
            <li>
              Revoke anytime in Agent connections. Signing out also ends this
              connection.
            </li>
          </ul>
          <div className="agent-oauth-actions">
            <button
              disabled={busy || !boardId}
              onClick={() => void decide("approve")}
            >
              {busy ? "Connecting…" : "Allow access"}
            </button>
            <button disabled={busy} onClick={() => void decide("deny")}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        !error && <p role="status">Checking the connection request…</p>
      )}
      {error && <p role="alert">{error}</p>}
      <a href="/oauth/connections">Manage agent connections</a>
    </main>
  );
}

type Connection = {
  id: string;
  clientName: string;
  boardId: string | null;
  projectName: string | null;
  permission: string;
  expiresAt: number;
};
export function AgentOAuthConnections() {
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api<Connection[]>("/api/agent-oauth/connections")
      .then(setConnections)
      .catch((e) => setError(e.message));
  }, []);
  async function revoke(id: string) {
    setBusy(id);
    setError("");
    try {
      await api("/api/agent-oauth/revoke", { id });
      setConnections((current) => current!.filter((c) => c.id !== id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  return (
    <main className="agent-oauth">
      <p className="agent-oauth-brand">CALQUE · INTERNAL</p>
      <h1>Agent connections</h1>
      <p>
        Add this server URL in your agent’s MCP settings, then sign in and
        choose one project or all of them.
      </p>
      <code className="agent-oauth-url">{location.origin}/mcp</code>
      <p>
        Read and edit sheets, add images and evidence, and preview a sheet through
        this connection. The local companion is optional for local file paths.
      </p>
      {connections === null && !error && (
        <p role="status">Loading connections…</p>
      )}
      {connections?.length === 0 && <p>No connected agents.</p>}
      {connections?.map((connection) => (
        <section className="agent-oauth-client" key={connection.id}>
          <strong>{connection.clientName}</strong>
          <p>
            {connection.projectName ?? "All your projects"} ·{" "}
            {connection.permission === "edit" ? "Read and edit" : "Read only"}
          </p>
          <p>Expires {new Date(connection.expiresAt).toLocaleString()}</p>
          <button disabled={!!busy} onClick={() => void revoke(connection.id)}>
            {busy === connection.id ? "Revoking…" : "Revoke access"}
          </button>
        </section>
      ))}
      {error && <p role="alert">{error}</p>}
      <a href="/app">Return to projects</a>
    </main>
  );
}
